// ============================================================================
// Clause-wise format registry — ISO/IEC 17025:2017
// Single source of truth for navigation, clause pages and the readiness
// dashboard. Codes/titles mirror the List of Records (8.4F-01) in the
// Sanber Labs Quality Manual.
//
// kind:
//   "custom"  — bespoke component already implemented in App.js
//   "table"   — register-style format rendered by <FormatTable>
//   "formdoc" — document-style format rendered by <FormatFormDoc>
// Formats marked pending:true are structural placeholders awaiting the
// laboratory's own soft-copy format; columns are provisional.
// ============================================================================

export const CLAUSES = [
  { n: 4, t: "General requirements" },
  { n: 5, t: "Structural requirements" },
  { n: 6, t: "Resource requirements" },
  { n: 7, t: "Process requirements" },
  { n: 8, t: "Management system" },
];

export const CLAUSE_TITLES = {
  "4": "General requirements — Impartiality & Confidentiality",
  "5": "Structural requirements",
  "6": "Resource requirements",
  "7": "Process requirements",
  "8": "Management system requirements",
};

const T = (cols, footer) => ({ kind: "table", columns: cols, footer: footer || "" });

// 8.3F-01 Master List of Documents — columns shared by the Internal and NABL-Guidelines registers.
// Matches the printed Format Manual page (S. No. is auto-numbered by <FormatTable>).
const MLD_COLS = [
  { key: "doc_name", label: "Document Name" },
  { key: "doc_no", label: "Document No." },
  { key: "issue", label: "Issue No. & Date" },
  { key: "amend", label: "Amend No. & Date" },
  { key: "signature", label: "Signature", type: "sign", fromPersonnel: true },
];

export const CLAUSE_FORMATS = {
  "4": [
    { coll: "impartiality_declarations", code: "4.1F-01", title: "Declaration Statement", section: "4.1 Impartiality", kind: "custom" },
    { coll: "impartiality_risks", code: "4.1F-02", title: "List of potential sources of risk and mitigation plan", section: "4.1 Impartiality", kind: "custom" },
    { coll: "confidentiality_customer", code: "4.2F-01", title: "Agreement of Confidentiality — Customer", section: "4.2 Confidentiality", kind: "custom" },
    { coll: "confidentiality_personnel", code: "4.2F-02", title: "Agreement of Confidentiality — Personnel", section: "4.2 Confidentiality", kind: "custom" },
    { coll: "confidentiality_auditor", code: "4.2F-03", title: "Impartiality & Confidentiality Agreement — External Auditor", section: "4.2 Confidentiality", kind: "custom" },
  ],
  "5": [
    { coll: "org_chart", code: "5.5A", title: "Organization Chart", section: "5.5 Organization structure", kind: "custom" },
  ],
  "6": [
    { coll: "personnel_record", code: "6.2F-01", title: "Personnel Record", section: "6.2 Personnel",
      ...T([
        { key: "name", label: "Name" }, { key: "designation", label: "Designation" },
        { key: "qualification", label: "Qualification" }, { key: "experience", label: "Experience" },
        { key: "doj", label: "Date of Joining" }, { key: "contact", label: "Contact" },
        { key: "docs", label: "Documents on file", type: "files" }, { key: "remarks", label: "Remarks" },
      ], "Maintained by Quality Manager") },
    { coll: "competence_recruitment", code: "6.2F-02", title: "Competence requirement for future recruitment", section: "6.2 Personnel", kind: "custom" },
    { coll: "competence_chart", code: "6.2F-03", title: "Competence Chart for Existing Staff", section: "6.2 Personnel", kind: "custom" },
    { coll: "training_calendar", code: "6.2F-04", title: "Training Calendar", section: "6.2 Personnel", kind: "custom" },
    { coll: "training_record", code: "6.2F-05", title: "Training Record", section: "6.2 Personnel", kind: "custom" },
    { coll: "authorization_letter", code: "6.2F-06", title: "Authorization Record", section: "6.2 Personnel", kind: "custom" },
    { coll: "job_description", code: "6.2F-07", title: "Job Description", section: "6.2 Personnel", kind: "custom" },
    { coll: "skill_matrix", code: "6.2F-08", title: "Skill Matrix", section: "6.2 Personnel",
      ...T([
        { key: "name", label: "Name" }, { key: "designation", label: "Designation" },
        { key: "test_skills", label: "Tests authorized / skill areas" },
        { key: "equipment_skills", label: "Equipment operating skills" },
        { key: "level", label: "Skill level" }, { key: "remarks", label: "Remarks" },
      ], "Prepared by (Quality Manager)") },
    { coll: "environmental_log", code: "6.3F-01", title: "Environmental Condition Record", section: "6.3 Facilities & environment", kind: "custom" },
    { coll: "housekeeping_log", code: "6.3F-02", title: "Housekeeping Record", section: "6.3 Facilities & environment", kind: "custom" },
    { coll: "equipment_list", code: "6.4F-01", title: "List of Laboratory Equipments", section: "6.4 Equipment", kind: "custom" },
    { coll: "equipment_logbook", code: "6.4F-02", title: "Equipment Logbook", section: "6.4 Equipment", kind: "custom" },
    { coll: "equipment_calibration", code: "6.4F-03", title: "Calibration Record of Laboratory Equipment", section: "6.4 Equipment", kind: "custom" },
    { coll: "intermediate_check", code: "6.4F-04", title: "Intermediate Check", section: "6.4 Equipment", kind: "custom" },
    { coll: "equipment_maintenance", code: "6.4F-05", title: "Testing Details Logbook of Equipment", section: "6.4 Equipment", kind: "custom" },
    { coll: "crm_list", code: "6.5F-01", title: "List of Certified Reference Materials", section: "6.5 Metrological traceability", kind: "custom" },
    { coll: "purchase_file", code: "6.6F-01", title: "Purchase File", section: "6.6 Externally provided products & services", tally: true,
      ...T([
        { key: "date", label: "Date" }, { key: "item", label: "Product / Service" },
        { key: "supplier", label: "Supplier" }, { key: "po_no", label: "PO / Ref No." },
        { key: "qty", label: "Qty" }, { key: "attachments", label: "PO / Invoice copy", type: "files" },
        { key: "remarks", label: "Remarks" },
      ], "Maintained by Quality Manager") },
    { coll: "service_verification", code: "6.6F-02", title: "Product / Service Verification Record", section: "6.6 Externally provided products & services", kind: "custom" },
    { coll: "supplier_evaluation", code: "6.6F-03", title: "Product / Service Provider Performance Evaluation Record", section: "6.6 Externally provided products & services", kind: "custom" },
  ],
  "7": [
    { coll: "test_requisition_internal", code: "7.1F-01", title: "Test Requisition Slip (Internal)", section: "7.1 Review of requests, tenders & contracts", pending: true, lims: true,
      ...T([
        { key: "date", label: "Date" }, { key: "req_no", label: "Requisition No." },
        { key: "source", label: "Department / Requested by" }, { key: "sample_desc", label: "Sample description" },
        { key: "tests", label: "Tests required" }, { key: "agreed_date", label: "Agreed completion date" },
        { key: "reviewed_by", label: "Reviewed by", type: "sign", fromPersonnel: true }, { key: "remarks", label: "Remarks" },
      ], "Contract review by Quality Manager") },
    { coll: "test_requisition_external", code: "7.1F-02", title: "Test Requisition Slip (External)", section: "7.1 Review of requests, tenders & contracts", pending: true, lims: true,
      ...T([
        { key: "date", label: "Date" }, { key: "req_no", label: "Requisition No." },
        { key: "customer", label: "Customer" }, { key: "sample_desc", label: "Sample description" },
        { key: "tests", label: "Tests required" }, { key: "method", label: "Method / Standard" },
        { key: "agreed_date", label: "Agreed completion date" },
        { key: "reviewed_by", label: "Reviewed by", type: "sign", fromPersonnel: true }, { key: "remarks", label: "Remarks" },
      ], "Contract review by Quality Manager") },
    { coll: "observation_sheet", code: "7.2F-01", title: "Observation / Calculation Sheet", section: "7.2 Selection, verification & validation of methods", pending: true, lims: true, approvalLabel: "Approved by",
      ...T([
        { key: "unique_id", label: "Unique ID No." }, { key: "req_no", label: "Requisition No." },
        { key: "sample_desc", label: "Sample\nDescription" },
        { key: "date_receipt", label: "Date of\nReceipt", type: "date" }, { key: "date_testing", label: "Date of\nTesting", type: "date" },
        { key: "method", label: "Method Used" },
        { key: "test", label: "Test / Parameter" }, { key: "observations", label: "Observations" },
        { key: "analyst", label: "Analyst", type: "sign", fromPersonnel: true },
      ], "Checked by (Technical Manager)") },
    { coll: "test_standard_file", code: "7.2F-02", title: "Test Standard File", section: "7.2 Selection, verification & validation of methods", pending: true,
      ...T([
        { key: "standard_no", label: "Standard No. (IS/ASTM/...)" }, { key: "title", label: "Title" },
        { key: "version", label: "Year / Version" }, { key: "discipline", label: "Discipline" },
        { key: "status", label: "Current / Superseded" }, { key: "copy", label: "Copy on file", type: "files" },
        { key: "remarks", label: "Remarks" },
      ], "Controlled by Quality Manager") },
    { coll: "master_sample_register", code: "7.4F-01", title: "Master Sample Register (Internal / External)", section: "7.4 Handling of test items", pending: true, lims: true, userFillable: true,
      headerGroups: [{ label: "Requisition No.", cols: ["req_internal", "req_external"] }],
      ...T([
        { key: "req_internal", label: "Internal" }, { key: "req_external", label: "External" },
        { key: "mtc_heat", label: "MTC No.\nHeat No." }, { key: "date_receipt", label: "Date of\nReceipt", type: "date" },
        { key: "item_desc", label: "Item Name\nSample Description" }, { key: "test_type", label: "Type of\nTesting" },
        { key: "unique_id", label: "Unique ID No." },
      ], "Maintained by In-charge Customer Support") },
    { coll: "job_card", code: "7.4F-02", title: "Job Card", section: "7.4 Handling of test items", kind: "custom" },
    { coll: "disposal_remnants", code: "7.4F-03", title: "Disposal of Remnants / Return to Customer", section: "7.4 Handling of test items",
      ...T([
        { key: "date", label: "Date" }, { key: "job_no", label: "Job Order No." },
        { key: "sample_desc", label: "Sample / Remnant description" }, { key: "mode", label: "Mode of Disposal / Returned to Customer" },
        { key: "authorized_by", label: "Authorized by", type: "sign", fromPersonnel: true }, { key: "remarks", label: "Remarks" },
      ]) },
    { coll: "amended_technical_records", code: "7.5F-01", title: "List of Amended Technical Records", section: "7.5 Technical records", pending: true, lims: true, userFillable: true,
      headerGroups: [{ label: "Requisition No.", cols: ["req_internal", "req_external"] }],
      ...T([
        { key: "req_internal", label: "Internal" }, { key: "req_external", label: "External" },
        { key: "mtc_heat", label: "MTC No.\nHeat No." }, { key: "date_receipt", label: "Date of\nReceipt", type: "date" },
        { key: "item_desc", label: "Item Name\nSample Description" }, { key: "test_type", label: "Type of\nTesting" },
        { key: "unique_id", label: "Unique ID No." },
      ], "Original record retained as per 7.5.2") },
    { coll: "mu_evaluation", code: "7.6F-01", title: "Measurement Uncertainty Evaluation Record", section: "7.6 Evaluation of measurement uncertainty", pending: true,
      ...T([
        { key: "parameter", label: "Test / Parameter" }, { key: "method", label: "Method" },
        { key: "mu", label: "Expanded MU (±)" }, { key: "k", label: "Coverage factor (k)" },
        { key: "date", label: "Date evaluated" }, { key: "evaluated_by", label: "Evaluated by" },
        { key: "budget", label: "MU budget sheet", type: "files" }, { key: "remarks", label: "Remarks" },
      ], "Reviewed by (Technical Manager)") },
    { coll: "ilc_plan", code: "7.7F-01", title: "Inter-laboratory Comparison (ILC) Plan", section: "7.7 Ensuring the validity of results", kind: "custom" },
    { coll: "ilc_pt_record", code: "7.7F-02", title: "Inter-laboratory Comparison / Proficiency Testing Record", section: "7.7 Ensuring the validity of results", pending: true,
      ...T([
        { key: "year", label: "Year" }, { key: "provider", label: "Scheme / Provider" },
        { key: "parameter", label: "Parameter" }, { key: "result", label: "Lab result" },
        { key: "assigned", label: "Assigned value" }, { key: "z_score", label: "z-score / En" },
        { key: "outcome", label: "Satisfactory / Action" }, { key: "report", label: "PT report", type: "files" },
        { key: "remarks", label: "Remarks" },
      ], "") },
    { coll: "replicate_testing", code: "7.7F-03", title: "Replicate Testing", section: "7.7 Ensuring the validity of results", kind: "custom" },
    { coll: "retesting_retained", code: "7.7F-04", title: "Re-testing of Retained Items", section: "7.7 Ensuring the validity of results", kind: "custom" },
    { coll: "blind_samples", code: "7.7F-06", title: "Testing of Blind Samples", section: "7.7 Ensuring the validity of results", kind: "custom" },
    { coll: "review_reported_results", code: "7.7F-07", title: "Review of Reported Results", section: "7.7 Ensuring the validity of results", pending: true,
      ...T([
        { key: "date", label: "Date" },
        { key: "report_no", label: "Report / Job No." },
        { key: "characteristic", label: "Characteristic reviewed" },
        { key: "observation", label: "Observation / trend noted" },
        { key: "conclusion", label: "Conclusion (satisfactory / action taken)" },
        { key: "reviewed_by", label: "Reviewed by", type: "sign", fromPersonnel: true },
        { key: "remarks", label: "Remarks" }, { key: "attachments", label: "Attachments", type: "files" },
      ], "") },
    { coll: "functional_check_record", code: "7.7F-08", title: "Functional Check of Measuring & Testing Equipment", section: "7.7 Ensuring the validity of results", kind: "custom" },
    { coll: "test_plan_record", code: "7.7F-09", title: "Test Plan & Record", section: "7.7 Ensuring the validity of results", kind: "custom" },
    { coll: "test_report_external", code: "7.8F-01", title: "Test Report — External (Office Copy)", section: "7.8 Reporting of results", pending: true, lims: true,
      ...T([
        { key: "report_no", label: "Report No." }, { key: "date", label: "Date of issue" },
        { key: "customer", label: "Customer" }, { key: "sample_desc", label: "Sample description" },
        { key: "tests", label: "Tests reported" }, { key: "issued_by", label: "Authorized by" },
        { key: "copy", label: "Report copy", type: "files" }, { key: "remarks", label: "Remarks" },
      ], "Office copies retained 3 years") },
    { coll: "test_report_internal", code: "7.8F-01A", title: "Test Report — Internal (Office Copy)", section: "7.8 Reporting of results", pending: true, lims: true,
      ...T([
        { key: "report_no", label: "Report No." }, { key: "date", label: "Date of issue" },
        { key: "source", label: "Department / Requested by" }, { key: "sample_desc", label: "Sample description" },
        { key: "tests", label: "Tests reported" }, { key: "issued_by", label: "Authorized by" },
        { key: "copy", label: "Report copy", type: "files" }, { key: "remarks", label: "Remarks" },
      ], "Office copies retained 3 years") },
    { coll: "complaint_resolution", code: "7.9F-01", title: "Complaint Resolution", section: "7.9 Complaints", kind: "custom" },
    { coll: "customer_feedback", code: "8.6F-02", title: "Customer Feedback Record", section: "7.9 Complaints", kind: "custom" },
    { coll: "nonconforming_work", code: "7.10F-01", title: "Nonconforming Testing Work Analysis", section: "7.10 Nonconforming work", kind: "custom" },
  ],
  "8": [
    { coll: "master_list_documents", code: "8.3F-01", title: "Master List of Documents", section: "8.3 Control of management system documents", kind: "table",
      // Two registers on one 8.3F-01 page — each with its own data + add/edit/delete:
      tables: [
        { coll: "master_list_internal", tabLabel: "Internal", title: "Master List of Documents (Internal)", columns: MLD_COLS, footer: "Controlled by Quality Manager" },
        { coll: "master_list_nabl", tabLabel: "NABL Guidelines", title: "Master List of Documents (NABL Guidelines)", columns: MLD_COLS, footer: "Controlled by Quality Manager" },
      ] },
    { coll: "doc_distribution", code: "8.3F-02", title: "Document Distribution Control Register", section: "8.3 Control of management system documents",
      ...T([
        { key: "doc_no", label: "Doc. No." }, { key: "title", label: "Document" },
        { key: "copy_no", label: "Copy No." }, { key: "issued_to", label: "Issued to" },
        { key: "issue_date", label: "Issue date" }, { key: "ack", label: "Acknowledgement", type: "sign", fromPersonnel: true },
        { key: "remarks", label: "Remarks" },
      ]) },
    { coll: "obsolete_record", code: "8.3F-03", title: "Obsolete Record", section: "8.3 Control of management system documents", system: true,
      ...T([
        { key: "doc_no", label: "Doc. No." }, { key: "title", label: "Document" },
        { key: "version", label: "Issue / Version withdrawn" }, { key: "date", label: "Date withdrawn" },
        { key: "reason", label: "Reason" }, { key: "disposition", label: "Retained / Destroyed" },
        { key: "remarks", label: "Remarks" },
      ], "One copy of obsolete documents retained and marked OBSOLETE") },
    { coll: "change_note", code: "8.3F-04", title: "Change Note", section: "8.3 Control of management system documents", system: true,
      ...T([
        { key: "date", label: "Date" }, { key: "doc_no", label: "Doc. No." },
        { key: "clause", label: "Section / Clause" }, { key: "change", label: "Change details" },
        { key: "reason", label: "Reason" }, { key: "approved_by", label: "Approved by", type: "sign", fromPersonnel: true },
      ]) },
    { coll: "list_of_records", code: "8.4F-01", title: "List of Records", section: "8.4 Control of records",
      ...T([
        { key: "ref_no", label: "Ref. No." }, { key: "record", label: "Record" },
        { key: "responsibility", label: "Responsibility" }, { key: "retention", label: "Retention period" },
        { key: "location", label: "Location / Storage" }, { key: "remarks", label: "Remarks" },
      ], "Maintained by Quality Manager") },
    { coll: "risk_register", code: "8.5F-01", title: "Potential Risk and Action Plan (Risk Register)", section: "8.5 Actions to address risks & opportunities",
      ...T([
        { key: "date", label: "Date identified" }, { key: "risk", label: "Risk / Opportunity" },
        { key: "category", label: "Category" }, { key: "impact", label: "Impact" },
        { key: "likelihood", label: "Likelihood" }, { key: "action", label: "Mitigation / Action plan" },
        { key: "responsibility", label: "Responsibility" }, { key: "target_date", label: "Target date" },
        { key: "status", label: "Status" },
      ], "Reviewed in Management Review") },
    { coll: "improvement_record", code: "8.6F-01", title: "Improvement Record", section: "8.6 Improvement", pending: true,
      ...T([
        { key: "date", label: "Date" }, { key: "source", label: "Source (audit/feedback/review/...)" },
        { key: "improvement", label: "Improvement identified" }, { key: "action", label: "Action implemented" },
        { key: "responsibility", label: "Responsibility" }, { key: "status", label: "Status" },
        { key: "remarks", label: "Remarks" },
      ], "Maintained by Quality Manager") },
    { coll: "internal_audit_plan", code: "8.8F-01", title: "Internal Audit Plan", section: "8.8 Internal audits",
      ...T([
        { key: "year", label: "Year" }, { key: "audit_no", label: "Audit No." },
        { key: "area", label: "Area / Clause(s)" }, { key: "auditor", label: "Auditor" },
        { key: "auditee", label: "Auditee" }, { key: "planned_date", label: "Planned date" },
        { key: "actual_date", label: "Actual date" }, { key: "status", label: "Status" },
      ], "Approved by (Quality Manager)"), kind: "custom" },
    { coll: "audit_observation", code: "8.8F-02", title: "Internal Audit Observation Sheet", section: "8.8 Internal audits",
      ...T([
        { key: "audit_no", label: "Audit No." }, { key: "date", label: "Date" },
        { key: "clause", label: "Clause" }, { key: "observation", label: "Observation / Finding" },
        { key: "type", label: "NC / Observation" }, { key: "auditee", label: "Auditee" },
        { key: "auditor", label: "Auditor", type: "sign", fromPersonnel: true }, { key: "remarks", label: "Remarks" },
      ]) },
    { coll: "internal_audit_report", code: "8.8F-03", title: "Internal Audit Report", section: "8.8 Internal audits",
      ...T([
        { key: "audit_no", label: "Audit No." }, { key: "date", label: "Date" },
        { key: "scope", label: "Scope / Areas audited" }, { key: "summary", label: "Summary of findings" },
        { key: "nc_count", label: "No. of NCs" }, { key: "auditor", label: "Auditor" },
        { key: "report", label: "Report copy", type: "files" },
        { key: "approved_by", label: "Approved by", type: "sign", fromPersonnel: true },
      ]), kind: "custom" },
    { coll: "audit_summary", code: "8.8F-04", title: "Internal Audit Summary", section: "8.8 Internal audits",
      ...T([
        { key: "audit_no", label: "Audit No." }, { key: "date", label: "Date" },
        { key: "total_ncs", label: "Total NCs" }, { key: "closed", label: "Closed" },
        { key: "open", label: "Open" }, { key: "remarks", label: "Effectiveness / Remarks" },
      ], "Presented in Management Review"), kind: "custom" },
    { coll: "mrm_agenda", code: "8.9F-01", title: "Management Review Meeting Agenda", section: "8.9 Management reviews", pending: true, kind: "formdoc",
      formdoc: {
        fields: [
          { key: "meeting_date", label: "Meeting date", type: "date" },
          { key: "venue", label: "Venue" },
          { key: "attendees", label: "Attendees", type: "textarea" },
          { key: "agenda", label: "Agenda points", type: "list" },
        ],
        signatures: [{ key: "qm_sign", label: "Quality Manager", fromSignatories: true }],
      } },
    { coll: "mrm_minutes", code: "8.9F-02", title: "Minutes of Management Review Meeting", section: "8.9 Management reviews", pending: true, kind: "formdoc",
      formdoc: {
        fields: [
          { key: "meeting_date", label: "Meeting date", type: "date" },
          { key: "attendees", label: "Attendees", type: "textarea" },
        ],
        innerTable: { label: "Discussion points", columns: [
          { key: "point", label: "Agenda point" }, { key: "discussion", label: "Discussion" },
          { key: "decision", label: "Decision / Action" }, { key: "responsibility", label: "Responsibility" },
          { key: "target_date", label: "Target date" },
        ] },
        signatures: [
          { key: "qm_sign", label: "Quality Manager", fromSignatories: true },
          { key: "ceo_sign", label: "CEO", fromSignatories: true },
        ],
      } },
  ],
};

// Notes shown on clause index pages for requirements whose records live elsewhere.
export const CLAUSE_NOTES = {
  "8": "8.7 Corrective Action records are maintained in 7.10F-01 (Nonconforming Testing Work Analysis) and the Internal Audit records, as per the Quality Manual.",
};

export function findFormat(coll) {
  for (const k of Object.keys(CLAUSE_FORMATS)) {
    const f = CLAUSE_FORMATS[k].find((x) => x.coll === coll);
    if (f) return { ...f, clause: k };
  }
  return null;
}

// Format code -> live app route, used to hyperlink references like "4.1F-01"
// wherever they appear inside the controlled documents.
export const CODE_ROUTES = (() => {
  const m = {};
  Object.keys(CLAUSE_FORMATS).forEach((n) =>
    CLAUSE_FORMATS[n].forEach((f) => { if (f.code) m[f.code] = { clause: n, coll: f.coll }; }));
  return m;
})();
